/*
 * option-swatches.js
 * Renders text-based product options (Scent, Shade) as named colour swatches:
 * a colour dot plus the value's name. The original radio inputs are kept and
 * still drive variant selection, so the theme's own variant logic is untouched.
 * Only the visible label is replaced.
 *
 * Also keeps the gallery and the options in step the other way round: when a
 * shopper swipes or clicks to an image that belongs to a variant, that variant
 * is selected.
 *
 * To change a colour, edit EXACT below. Anything not listed falls back to a
 * keyword match (KEYWORDS), then to a neutral chip.
 */
(function () {
  'use strict';

  // Option names that become swatches. Compared lower-case, trimmed.
  var SWATCH_OPTIONS = ['scent', 'shade', 'color', 'colour'];

  // Exact value -> colour. Takes priority over keywords.
  var EXACT = {
    'white musk': '#F2EDE6',
    'green musk': '#6E9B5A',
    'green tea': '#8FAE68',
    'green tea musk': '#8FAE68',
    'barbie musk': '#E8478E',
    'bridal musk': '#EFDCD3',
    'feminine musk': '#F6C8D6',
    'bebe musk': '#FBE3E8',
    'baby powder': '#FBE9EE',
    'dove': '#E9E6E1',
    'floral': '#E39BB4',
    'romance': '#E05C7B',
    'sweet candy': '#FF7FB0',
    'bubble gum': '#FF8FB1',
    'pink candy': '#FF9EC4',
    'pink blossom': '#F6B7CB',
    'exotic pink': '#F0559B',
    'sun kissed': '#E8A85C',
    'bronzed paradise': '#B87333',
    'beach dreams': '#7EC8C4',
    'citrus fresh': '#F6C445',
    'soft clean scent': '#E4EFF5',
    'fresh laundry': '#CFE3EE',
    'clean cotton': '#EDF3F7',
    'paris glam': '#C9A227',
    'sexy oud': '#5C2B2B',
    'secret oud': '#4A3B52',
    'sweet oud': '#8A5A2B',
    'attractive oud': '#6B4423',
    'rose oud': '#8C4A52',
    'oud seduction': '#6B4423',
    'oud': '#7A5230',
    'rainbow': '#9B6BE8',
    'chocolate fudge': '#5D4037',
    'milk chocolate': '#8B5E3C',
    'blueberry cheesecake': '#5C6BC0',
    'cherry cheesecake': '#C2334D',
    'berry bliss': '#7B2D4E',
    'berry coffee': '#6B4A4A',
    'peach sorbet': '#FFB48A',
    'peach raspberry': '#F06292',
    'vanilla dream': '#F1E4C3',
    'coconut vanilla': '#F3E8D2',
    'coconut breeze': '#F5F0E6',
    'lavender bliss': '#B39DDB',
    'lavender dreams': '#C3B2E6',
    'rose garden': '#D4788A',
    'rose bouquet': '#D98494',
    'marshmallow': '#FBE6EC',
    'unicorn': '#C79BF2',
    'almond': '#E4CBA5',
    'pink flamingo': '#FF7FA8'
  };

  // Keyword -> colour. First match wins, so order matters (longest ideas first).
  var KEYWORDS = [
    ['strawberry', '#F04C6B'],
    ['raspberry', '#C2185B'],
    ['pomegranate', '#9E1B32'],
    ['cherry', '#C2334D'],
    ['blueberry', '#5C6BC0'],
    ['berry', '#7B2D4E'],
    ['mango', '#F2A03D'],
    ['peach', '#FFB48A'],
    ['orange', '#F57C00'],
    ['citrus', '#F6C445'],
    ['lemon', '#EFD358'],
    ['coconut', '#F3E8D2'],
    ['vanilla', '#F1E4C3'],
    ['caramel', '#C68642'],
    ['chocolate', '#5D4037'],
    ['coffee', '#6B4A4A'],
    ['lavender', '#B39DDB'],
    ['rose', '#D4788A'],
    ['floral', '#E39BB4'],
    ['jasmine', '#F6EEDC'],
    ['green', '#6E9B5A'],
    ['oud', '#7A5230'],
    ['musk', '#E7C7D2'],
    ['powder', '#FBE9EE'],
    ['barbie', '#E8478E'],
    ['pink', '#F48FB1'],
    ['red', '#D32F2F'],
    ['purple', '#8E63C4'],
    ['blue', '#4A7FD6'],
    ['white', '#F2EDE6'],
    ['black', '#2B2B2B'],
    ['gold', '#C9A227'],
    ['summer', '#F0B45C'],
    ['glow', '#F3C77B'],
    ['secret', '#4A3B52'],
    ['night', '#3B3A52'],
    ['bloom', '#E4A0BC'],
    ['fruity', '#F2708A'],
    ['sweet', '#FF9EC4'],
    ['clean', '#E4EFF5'],
    ['fresh', '#DCEAF2']
  ];

  var FALLBACK = '#DED8D2';

  function colourFor(value) {
    var v = String(value || '').trim().toLowerCase();
    if (!v) return FALLBACK;
    if (EXACT[v]) return EXACT[v];
    for (var i = 0; i < KEYWORDS.length; i++) {
      if (v.indexOf(KEYWORDS[i][0]) !== -1) return KEYWORDS[i][1];
    }
    return FALLBACK;
  }

  function optionName(fieldset) {
    var legend = fieldset.querySelector('legend');
    if (legend) return legend.textContent.trim();
    var input = fieldset.querySelector('input[type="radio"]');
    return input ? (input.name || '') : '';
  }

  function build(fieldset) {
    if (fieldset.dataset.glSwatches === 'on') return;

    var name = optionName(fieldset);
    if (SWATCH_OPTIONS.indexOf(name.toLowerCase()) === -1) return;

    var inputs = [].slice.call(fieldset.querySelectorAll('input[type="radio"]'));
    if (inputs.length < 2) return;

    fieldset.dataset.glSwatches = 'on';

    // Readout of the selected value, next to the option name.
    var legend = fieldset.querySelector('legend');
    var readout = document.createElement('span');
    readout.className = 'gl-sw__value';
    if (legend) {
      legend.classList.add('gl-sw__head');
      legend.appendChild(readout);
    }

    var list = document.createElement('div');
    list.className = 'gl-sw__list';

    inputs.forEach(function (input) {
      var value = input.value;
      var label = fieldset.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');

      var item = document.createElement('div');
      item.className = 'gl-sw__item';

      var chip = document.createElement('label');
      chip.className = 'gl-sw__chip';
      chip.setAttribute('for', input.id);
      chip.setAttribute('title', value);
      chip.style.setProperty('--gl-sw', colourFor(value));

      var dot = document.createElement('span');
      dot.className = 'gl-sw__dot';
      dot.setAttribute('aria-hidden', 'true');
      chip.appendChild(dot);

      var text = document.createElement('span');
      text.className = 'gl-sw__name';
      text.textContent = value;
      chip.appendChild(text);

      // Carry over the theme's sold-out marker if the original label had one.
      if (label && /sold out|unavailable/i.test(label.textContent)) {
        chip.classList.add('is-unavailable');
      }

      item.appendChild(input);
      item.appendChild(chip);
      list.appendChild(item);

      if (label && label.parentNode) label.parentNode.removeChild(label);
    });

    fieldset.appendChild(list);

    function sync() {
      var selected = '';
      inputs.forEach(function (input) {
        var chip = input.nextElementSibling;
        if (!chip) return;
        if (input.checked) {
          chip.classList.add('is-selected');
          selected = input.value;
        } else {
          chip.classList.remove('is-selected');
        }
      });
      readout.textContent = selected;
    }

    fieldset.addEventListener('change', sync);
    document.addEventListener('variant:change', sync);
    sync();
  }

  /* ---------- Gallery -> variant ---------- */

  function pickerFor(el) {
    var section = el.closest('.shopify-section, section, product-info, .product') || document;
    return section.querySelector('variant-radios, variant-selects');
  }

  function variantsOf(picker) {
    if (picker._glVariants) return picker._glVariants;
    var json = picker.querySelector('script[type="application/json"]');
    try { picker._glVariants = json ? JSON.parse(json.textContent) : []; } catch (e) { picker._glVariants = []; }
    return picker._glVariants;
  }

  function mediaIdOf(variant) {
    if (variant.featured_media && variant.featured_media.id) return String(variant.featured_media.id);
    return '';
  }

  // Current selection per option position, read from the checked radios.
  function currentOptions(picker) {
    return [].slice.call(picker.querySelectorAll('fieldset')).map(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function selectForMedia(el, rawId) {
    var picker = pickerFor(el);
    if (!picker || !rawId) return;
    var mediaId = String(rawId).split('-').pop();
    var variants = variantsOf(picker);
    var current = currentOptions(picker);

    var best = null;
    var bestScore = -1;
    variants.forEach(function (v) {
      if (mediaIdOf(v) !== mediaId) return;
      // Prefer the variant that keeps the shopper's other choices (e.g. size).
      var score = 0;
      (v.options || []).forEach(function (val, i) { if (current[i] === val) score++; });
      if (v.available) score += 0.5;
      if (score > bestScore) { best = v; bestScore = score; }
    });
    if (!best) return;

    var fieldsets = picker.querySelectorAll('fieldset');
    (best.options || []).forEach(function (val, i) {
      if (current[i] === val || !fieldsets[i]) return;
      var radios = fieldsets[i].querySelectorAll('input[type="radio"]');
      for (var r = 0; r < radios.length; r++) {
        if (radios[r].value === val) { radios[r].click(); break; }
      }
    });
  }

  // Share of a slide that is inside the gallery's visible area (0..1).
  function shownShare(slide, box) {
    var r = slide.getBoundingClientRect();
    if (!r.width) return 0;
    var overlap = Math.min(r.right, box.right) - Math.max(r.left, box.left);
    return Math.max(0, overlap) / r.width;
  }

  // The theme's slideChanged reports the slide nearest the scroll start, which
  // is wrong for the last slides (they can't scroll fully to the left edge).
  // Work out what is actually on screen instead.
  function onSwipe(gallery, viewer) {
    var picker = pickerFor(gallery);
    var list = viewer.querySelector('.slider, ul');
    if (!picker || !list) return;
    var box = list.getBoundingClientRect();
    var slides = [].slice.call(list.querySelectorAll(':scope > [data-media-id]'));
    if (!slides.length) return;

    // Leave the selection alone while its own image is still in view.
    var current = currentOptions(picker).join('\u0000');
    var selected = variantsOf(picker).filter(function (v) { return (v.options || []).join('\u0000') === current; })[0];
    var selectedMedia = selected ? mediaIdOf(selected) : '';
    if (selectedMedia) {
      var own = slides.filter(function (s) { return s.dataset.mediaId.split('-').pop() === selectedMedia; })[0];
      if (own && shownShare(own, box) >= 0.5) return;
    }

    var best = null;
    var bestShare = 0;
    slides.forEach(function (s) {
      var share = shownShare(s, box);
      if (share > bestShare) { best = s; bestShare = share; }
    });
    if (best && bestShare >= 0.5) selectForMedia(gallery, best.dataset.mediaId);
  }

  function bindGallery(gallery) {
    if (gallery.dataset.glGallerySync === 'on') return;
    gallery.dataset.glGallerySync = 'on';

    // The theme moves a variant's image to the front of the gallery when the
    // variant changes. Keep the original order and just scroll to the image,
    // so swiping through scents doesn't reshuffle the slides.
    var setActive = gallery.setActiveMedia;
    if (typeof setActive === 'function') {
      gallery.setActiveMedia = function (mediaId) {
        return setActive.call(this, mediaId, false);
      };
    }

    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (viewer) {
      var timer = null;
      viewer.addEventListener('slideChanged', function () {
        clearTimeout(timer);
        // Wait for the swipe to settle so passing slides don't flip the selection.
        timer = setTimeout(function () { onSwipe(gallery, viewer); }, 550);
      });
    }

    gallery.addEventListener('click', function (event) {
      var thumb = event.target.closest('[id^="GalleryThumbnails"] [data-target]');
      if (thumb && event.target.closest('button')) selectForMedia(gallery, thumb.dataset.target);
    });
  }

  function scan() {
    var sets = document.querySelectorAll('fieldset.product-form__input');
    for (var i = 0; i < sets.length; i++) build(sets[i]);
    var galleries = document.querySelectorAll('media-gallery');
    for (var g = 0; g < galleries.length; g++) bindGallery(galleries[g]);
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; scan(); }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);
  document.addEventListener('shopify:section:load', schedule);
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
